import React from 'react';
import type { OrderBumpCard } from '../../offer.types';

interface OrderBumpSectionProps {
  bumps: OrderBumpCard[];
  acceptedNanoIds: string[];
  onToggle: (nextAccepted: string[]) => void;
}

const formatBRL = (value: number) =>
  Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const OrderBumpSection: React.FC<OrderBumpSectionProps> = ({ bumps, acceptedNanoIds, onToggle }) => {
  if (!bumps || bumps.length === 0) return null;

  const acceptedSet = new Set(acceptedNanoIds);

  const toggle = (nanoId: string) => {
    const isAccepted = acceptedSet.has(nanoId);
    const next = isAccepted
      ? acceptedNanoIds.filter((id) => id !== nanoId)
      : [...acceptedNanoIds, nanoId];
    onToggle(next);
  };

  const availableCount = bumps.filter((b) => !acceptedSet.has(b.nanoId)).length;
  const badgeLabel =
    availableCount > 0
      ? `VOCÊ TEM ${availableCount} OFERTA${availableCount > 1 ? 'S' : ''}!`
      : 'OFERTAS ADICIONADAS';

  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <span className="inline-block bg-yellow-400 text-yellow-900 font-bold text-xs tracking-wide px-3 py-1 rounded">
          {badgeLabel}
        </span>
      </div>

      {bumps.map((bump) => {
        const isAccepted = acceptedSet.has(bump.nanoId);
        return (
          <div
            key={bump.nanoId}
            className={`rounded-lg p-3 bg-gray-50 dark:bg-gray-800 border-2 border-dashed ${
              isAccepted ? 'border-green-500' : 'border-pink-500'
            }`}
          >
            <div className="flex gap-3">
              {bump.imageUrl && (
                <img
                  src={bump.imageUrl}
                  alt={bump.title}
                  className="w-20 h-20 object-cover rounded-md flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 leading-tight">
                  {bump.title}
                </h3>
                <div className="mt-1 flex items-baseline gap-2">
                  {bump.compareAtPrice != null && bump.compareAtPrice > bump.price && (
                    <span className="text-xs line-through text-gray-500">
                      {formatBRL(bump.compareAtPrice)}
                    </span>
                  )}
                  <span className="text-lg font-bold text-green-600">
                    {formatBRL(bump.price)}
                  </span>
                </div>
              </div>
            </div>

            {bump.description && (
              <p className="mt-2 text-xs text-gray-700 dark:text-gray-300 leading-snug">
                {bump.description}
              </p>
            )}

            <button
              type="button"
              onClick={() => toggle(bump.nanoId)}
              className="mt-3 w-full py-2.5 rounded-md font-semibold text-sm bg-black text-white transition-colors hover:bg-gray-800"
            >
              {isAccepted ? '✓ Oferta Adicionada' : '+ Adicionar ao Pedido'}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default OrderBumpSection;
